// frontend/src/services/api/modules/admin.js
import { API_URL } from '../config';
import { getAuthHeaders, fetchWrapper } from '../utils/fetchWrapper';

export const obtenerRegistros = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/registros`, { headers });
    return await response.json();
  } catch (error) {
    console.error("Error obteniendo registros:", error);
    return null;
  }
};

/**
 * Crea registros en lote
 * @param {number} count - Cantidad de registros a crear (1-50)
 * @param {string} tipo - Tipo de objeto: 'mascota', 'llavero', 'coleccionable'
 * @param {string} plan - Plan: 'light', 'standard', 'premium' (coleccionables siempre son light)
 * @param {boolean} conCodigoActivacion - Si genera código de activación rascable
 * @param {Array} datosAutenticidad - Array con datos de autenticidad para cada coleccionable
 * @returns {Promise<Blob>} CSV con los registros creados
 */
export const crearRegistrosBatch = async (count, tipo, plan, conCodigoActivacion = true, datosAutenticidad = null) => {
  const headers = await getAuthHeaders();

  const bodyData = { count, tipo, plan, conCodigoActivacion };

  // Agregar datos de autenticidad solo si es coleccionable y hay datos
  if (tipo === 'coleccionable' && Array.isArray(datosAutenticidad)) {
    bodyData.datosAutenticidad = datosAutenticidad;
  }

  const response = await fetchWrapper(`${API_URL}/api/objetos/batch`, {
    method: "POST",
    headers,
    body: JSON.stringify(bodyData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Error al crear en lote");
  }
  return await response.blob();
};

export const agregarRegistro = async (nuevoRegistro) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/agregar`, {
      method: "POST",
      headers,
      body: JSON.stringify(nuevoRegistro),
    });
    return await response.json();
  } catch (error) {
    console.error("Error agregando registro:", error);
    return null;
  }
};

export const obtenerRegistrosPaginados = async (filtros = {}, pageSize, pagina) => {
  try {
    let url = `${API_URL}/api/admin/registros?paginado=true&pageSize=${pageSize}&pagina=${pagina}`;
    
    if (filtros.tipo) url += `&tipo=${encodeURIComponent(filtros.tipo)}`;
    if (filtros.correo) url += `&correo=${encodeURIComponent(filtros.correo)}`;
    if (filtros.estado) url += `&estado=${encodeURIComponent(filtros.estado)}`;
    if (filtros.plan) url += `&plan=${encodeURIComponent(filtros.plan)}`;
    if (filtros.propietario) url += `&propietario=${encodeURIComponent(filtros.propietario)}`;
    if (filtros.folio) url += `&folio=${encodeURIComponent(filtros.folio)}`;
    if (filtros.fechaDesde) url += `&fechaDesde=${encodeURIComponent(filtros.fechaDesde)}`;
    if (filtros.fechaHasta) url += `&fechaHasta=${encodeURIComponent(filtros.fechaHasta)}`;
    if (filtros.texto) url += `&texto=${encodeURIComponent(filtros.texto)}`; // por si usas búsqueda libre
    if (filtros.estadoImpresion) url += `&estadoImpresion=${encodeURIComponent(filtros.estadoImpresion)}`;
    if (filtros.loteImpresion) url += `&loteImpresion=${encodeURIComponent(filtros.loteImpresion)}`;

    const headers = await getAuthHeaders();
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      const error = await response.json();
      console.error('Error:', error);
      return { error: error.error || 'Error al obtener registros' };
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error obteniendo registros paginados:", error);
    return { error: 'Error de conexión' };
  }
};

export const reactivarRegistro = async (id) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/objetos/ligar/reactivar/${id}`, {
      method: "PUT",
      headers,
    });
    return await response.json();
  } catch (error) {
    console.error("Error reactivando registro:", error);
    return null;
  }
};

export const getConfigFromBackend = async () => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/config`, { headers });
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      return { error: errorData.error || "Error al obtener config" };
    }
    return await resp.json();
  } catch (error) {
    console.error("Error getConfigFromBackend =>", error);
    return { error: "Fallo de conexión" };
  }
};

export const updateConfigOnBackend = async (partialConfig) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/config`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(partialConfig),
    });
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      return { error: errorData.error || "Error al actualizar config" };
    }
    return await resp.json();
  } catch (error) {
    console.error("Error updateConfigOnBackend =>", error);
    return { error: "Fallo de conexión" };
  }
};

/**
 * Obtener lista paginada de veterinarios para admin
 */
export const obtenerVeterinariosAdmin = async (filtros = {}, pageSize = 20, pagina = 1) => {
  try {
    let url = `${API_URL}/api/admin/veterinarios?page=${pagina}&pageSize=${pageSize}`;

    if (filtros.search) url += `&search=${encodeURIComponent(filtros.search)}`;
    if (filtros.estado) url += `&estado=${encodeURIComponent(filtros.estado)}`;

    const headers = await getAuthHeaders();
    const response = await fetchWrapper(url, { headers });

    if (!response.ok) {
      const error = await response.json();
      return { error: error.error || 'Error al obtener veterinarios' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error obtenerVeterinariosAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Obtener lista paginada de proveedores para admin
 */
export const obtenerProveedoresAdmin = async (filtros = {}, pageSize = 20, pagina = 1) => {
  try {
    let url = `${API_URL}/api/admin/proveedores?page=${pagina}&pageSize=${pageSize}`;

    if (filtros.search) url += `&search=${encodeURIComponent(filtros.search)}`;
    if (filtros.estado) url += `&estado=${encodeURIComponent(filtros.estado)}`;

    const headers = await getAuthHeaders();
    const response = await fetchWrapper(url, { headers });

    if (!response.ok) {
      const error = await response.json();
      return { error: error.error || 'Error al obtener proveedores' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error obtenerProveedoresAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

// ===== FUNCIONES PARA EDICIÓN DE REGISTROS (ADMIN) =====

/**
 * Obtener un objeto específico para edición (admin)
 * @param {string} objetoId - ID del objeto a obtener
 * @returns {Promise<Object>} Datos del objeto
 */
export const obtenerObjetoAdmin = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}`, { headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al obtener el objeto' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error obtenerObjetoAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Actualizar un objeto desde el admin (respeta campos inmutables)
 * @param {string} objetoId - ID del objeto a actualizar
 * @param {Object} cambios - Campos a actualizar
 * @returns {Promise<Object>} Resultado de la operación
 */
export const actualizarObjetoAdmin = async (objetoId, cambios) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(cambios)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al actualizar el objeto' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error actualizarObjetoAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Toggle visibilidad del objeto (oculto/visible para el usuario)
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<Object>} { success, oculto, message }
 */
export const toggleOcultoAdmin = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}/ocultar`, {
      method: 'PATCH',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al cambiar visibilidad' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error toggleOcultoAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Resetear código de activación del objeto
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<Object>} { success, codigoNuevo, message }
 */
export const resetearCodigoAdmin = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}/resetear-codigo`, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al resetear código' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error resetearCodigoAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Eliminar un objeto permanentemente
 * @param {string} objetoId - ID del objeto a eliminar
 * @param {string} confirmacion - Debe ser 'ELIMINAR_PERMANENTEMENTE'
 * @returns {Promise<Object>} { success, message, folioEliminado, tipoEliminado }
 */
export const eliminarObjetoAdmin = async (objetoId, confirmacion) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ confirmacion })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al eliminar el objeto' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error eliminarObjetoAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Extender el plan de un objeto
 * @param {string} objetoId - ID del objeto
 * @param {number} mesesAdicionales - Meses a agregar al plan
 * @returns {Promise<Object>} Resultado de la operación
 */
export const extenderPlan = async (objetoId, mesesAdicionales) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}/extender-plan`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ mesesAdicionales })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al extender plan' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error extenderPlan:", error);
    return { error: 'Error de conexión' };
  }
};

// ===== FUNCIONES PARA CONTROL DE IMPRESIÓN DE TAGS =====

/**
 * Marcar múltiples objetos como impresos (batch)
 * @param {Array<string>} objetoIds - Array de IDs de objetos a marcar
 * @param {string} loteImpresion - Nombre del lote (opcional)
 * @returns {Promise<Object>} { success, lote, marcados }
 */
export const marcarObjetosImpresos = async (objetoIds, loteImpresion = null) => {
  try {
    const headers = await getAuthHeaders();
    const body = { objetoIds };
    if (loteImpresion) {
      body.loteImpresion = loteImpresion;
    }

    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/marcar-impresos`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al marcar como impresos' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error marcarObjetosImpresos:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Marcar un objeto como pendiente de impresión (para reimprimir)
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<Object>} { success, message }
 */
export const marcarObjetoPendiente = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}/marcar-pendiente`, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al marcar como pendiente' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error marcarObjetoPendiente:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Obtener lista de lotes de impresión únicos
 * @returns {Promise<Object>} { success, data: [{ lote, cantidad, fechaImpresion }] }
 */
export const obtenerLotes = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/lotes`, { headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al obtener lotes' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error obtenerLotes:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Toggle modo demo del objeto (esDemo)
 * @param {string} objetoId - ID del objeto
 * @returns {Promise<Object>} { success, esDemo, message }
 */
export const toggleDemoAdmin = async (objetoId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}/toggle-demo`, {
      method: 'PATCH',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al cambiar modo demo' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error toggleDemoAdmin:", error);
    return { error: 'Error de conexión' };
  }
};

/**
 * Resetear datos específicos de un objeto (vacunas, visitas, etc.)
 * @param {string} objetoId - ID del objeto
 * @param {Object} flags - { vacunas, visitas, desparasitaciones, citas, recordatorios, lugaresConfianza, veterinarios, documentos }
 * @returns {Promise<Object>} { success, message, detalle }
 */
export const resetDatosAdmin = async (objetoId, flags) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetchWrapper(`${API_URL}/api/admin/objetos/${objetoId}/reset-datos`, {
      method: 'POST',
      headers,
      body: JSON.stringify(flags)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { error: errorData.error || 'Error al resetear datos' };
    }

    return await response.json();
  } catch (error) {
    console.error("Error resetDatosAdmin:", error);
    return { error: 'Error de conexión' };
  }
};